/**
 * components/AlgorithmPanels.jsx  —  NISH FSLP
 * ─────────────────────────────────────────────────────────────────────────────
 * Per-algorithm deep analytics panels for the ChildrenView AnalyticsTab.
 *
 * Exports:
 *   <AlgorithmAnalysisSection summary={summary} />
 *     → summary must be the state object from:
 *       GET /api/flashcard-sessions/:childId/analytics
 *       (the full `{ analytics: { sm2Analytics, bktAnalytics, eloAnalytics, ... } }` response)
 *
 * Usage in ChildrenView.jsx — inside AnalyticsTab, at the very bottom:
 *   import { AlgorithmAnalysisSection } from './AlgorithmPanels';
 *   ...
 *   <AlgorithmAnalysisSection summary={summary} />
 *
 * Algorithms covered:
 *   SM-2   — Ease Factor, review intervals, overdue cards, hardest/strongest cards
 *   BKT    — masteryProb distribution, pSlip (forget risk), per-card knowledge state
 *   ELO    — child rating vs card difficulty, comfort/challenge zone, per-card gap
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { useState } from 'react';

// ─────────────────────────────────────────────────────────────────────────────
//  Shared mini-primitives
// ─────────────────────────────────────────────────────────────────────────────

function AlgStat({ icon, val, label, color = '#1a2b49', bg = '#f0f4ff', sub }) {
  return (
    <div style={{ borderRadius: 12, padding: '12px 14px', background: bg, border: `1px solid ${color}22`, textAlign: 'center' }}>
      <div style={{ fontSize: 18, marginBottom: 3 }}>{icon}</div>
      <div style={{ fontSize: 22, fontWeight: 900, color, lineHeight: 1 }}>{val}</div>
      <div style={{ fontSize: 9, fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 3 }}>{label}</div>
      {sub && <div style={{ fontSize: 10, color: '#8a9ab5', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function MiniBar({ pct, color, height = 7 }) {
  return (
    <div style={{ height, background: '#eef1f7', borderRadius: 4, overflow: 'hidden' }}>
      <div style={{
        width: `${Math.min(100, Math.max(0, pct))}%`, height: '100%',
        background: color, borderRadius: 4, transition: 'width .4s'
      }} />
    </div>
  );
}

function SectionHead({ label }) {
  return (
    <div style={{
      fontWeight: 800, fontSize: 10, color: '#8a9ab5',
      letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 10
    }}>
      {label}
    </div>
  );
}

function InfoBox({ bg, border, color, children }) {
  return (
    <div style={{
      background: bg, border: `1.5px solid ${border}`,
      borderRadius: 10, padding: '10px 14px',
      fontSize: 11, color, lineHeight: 1.6
    }}>
      {children}
    </div>
  );
}

function Panel({ children }) {
  return (
    <div style={{
      background: 'white', borderRadius: 12,
      padding: '14px 18px', border: '1.5px solid #e2e8f0'
    }}>
      {children}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  SM-2  Panel
//  Data shape (from sm2Analytics in analytics response):
//    { avgEF, avgInterval, overdueCount, efBuckets{veryHard,hard,medium,easy},
//      hardestCards[], strongestCards[], setHealth[], totalCards }
// ─────────────────────────────────────────────────────────────────────────────

function SM2Panel({ data }) {
  if (!data) return (
    <div style={{ padding: '28px 0', textAlign: 'center', color: '#8a9ab5', fontSize: 12 }}>
      No SM-2 data yet — complete at least one session to see spaced repetition analysis.
    </div>
  );

  const { avgEF, avgInterval, overdueCount, efBuckets, hardestCards, strongestCards, setHealth, totalCards } = data;
  const efTotal    = efBuckets.veryHard + efBuckets.hard + efBuckets.medium + efBuckets.easy;
  const efColor    = avgEF < 1.7 ? '#C0392B' : avgEF < 2.2 ? '#D4AC0D' : '#1B6B45';

  const EFBucket = ({ label, count, color }) => {
    const pct = efTotal > 0 ? (count / efTotal) * 100 : 0;
    return (
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: '#1a2b49' }}>{label}</span>
          <span style={{ fontSize: 11, fontWeight: 900, color }}>{count} cards ({Math.round(pct)}%)</span>
        </div>
        <MiniBar pct={pct} color={color} height={8} />
      </div>
    );
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <InfoBox bg="#EBF5FF" border="#AED6F1" color="#1A4A6E">
        <strong>SM-2 (SuperMemo 2)</strong> adapts the review schedule per card.
        The <em>Ease Factor (EF)</em> starts at 2.5 and drops when a child struggles —
        lower EF = harder card, sooner review.
      </InfoBox>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        <AlgStat icon="📊" val={avgEF}    label="Avg Ease Factor" color={efColor} bg={`${efColor}12`} sub="2.5 = default" />
        <AlgStat icon="📅" val={`${avgInterval}d`} label="Avg Interval" color="#1A4A6E" bg="#EBF4FB" sub="days between reviews" />
        <AlgStat icon="⏰" val={overdueCount} label="Overdue Cards"
          color={overdueCount > 0 ? '#C0392B' : '#1B6B45'}
          bg={overdueCount > 0 ? '#FDEDEC' : '#EDF7F2'}
          sub="need review now" />
        <AlgStat icon="🃏" val={totalCards} label="Total Cards" color="#7B2D8B" bg="#F5EEF8" />
      </div>

      {/* EF Distribution */}
      <Panel>
        <SectionHead label="Ease Factor Distribution" />
        <EFBucket label="🔴 Very Hard  (EF < 1.5)"   count={efBuckets.veryHard} color="#C0392B" />
        <EFBucket label="🟠 Hard       (1.5 – 2.0)"  count={efBuckets.hard}     color="#E65100" />
        <EFBucket label="🟡 Medium     (2.0 – 2.5)"  count={efBuckets.medium}   color="#D4AC0D" />
        <EFBucket label="🟢 Easy       (EF ≥ 2.5)"   count={efBuckets.easy}     color="#1B6B45" />
        <div style={{ marginTop: 10, fontSize: 10, color: '#8a9ab5', lineHeight: 1.6 }}>
          Healthy distribution = most cards in Medium–Easy. Many Very Hard cards = needs more practice time.
        </div>
      </Panel>

      {/* Per-set health */}
      {setHealth?.length > 0 && (
        <Panel>
          <SectionHead label="SM-2 Health by Flashcard Set" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {setHealth.map((s, i) => {
              const col = s.score >= 70 ? '#1B6B45' : s.score >= 40 ? '#D4AC0D' : '#C0392B';
              return (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1a2b49', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.setTitle || 'Untitled'}
                    </span>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0, marginLeft: 10 }}>
                      {s.overdue > 0 && (
                        <span style={{ fontSize: 9, background: '#FDEDEC', color: '#C0392B', fontWeight: 700, padding: '1px 6px', borderRadius: 8 }}>
                          ⏰ {s.overdue} due
                        </span>
                      )}
                      <span style={{ fontSize: 12, fontWeight: 900, color: col }}>{s.score}%</span>
                    </div>
                  </div>
                  <MiniBar pct={s.score} color={col} />
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* Hardest vs Strongest */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <Panel>
          <SectionHead label="🔴 Hardest Cards (Lowest EF)" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {hardestCards.length === 0
              ? <div style={{ fontSize: 11, color: '#8a9ab5' }}>No card data yet</div>
              : hardestCards.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: '#fafbff', border: '1px solid #f0f4f8' }}>
                  <span style={{ fontSize: 10, fontWeight: 900, color: '#C0392B', minWidth: 16 }}>#{i + 1}</span>
                  <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#1a2b49', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.cardLabel || c.cardId}
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, background: '#FDEDEC', color: '#C0392B', padding: '1px 6px', borderRadius: 8 }}>EF {c.ef}</span>
                    <span style={{ fontSize: 9, color: '#8a9ab5', fontWeight: 600 }}>{c.interval}d</span>
                    {c.overdue && <span style={{ fontSize: 9, background: '#FFF3E0', color: '#E65100', fontWeight: 700, padding: '1px 5px', borderRadius: 6 }}>⏰</span>}
                  </div>
                </div>
              ))
            }
          </div>
        </Panel>

        <Panel>
          <SectionHead label="🟢 Strongest Cards (EF × Interval)" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {strongestCards.length === 0
              ? <div style={{ fontSize: 11, color: '#8a9ab5' }}>No card data yet</div>
              : strongestCards.map((c, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderRadius: 8, background: '#fafbff', border: '1px solid #f0f4f8' }}>
                  <span style={{ fontSize: 10, fontWeight: 900, color: '#1B6B45', minWidth: 16 }}>#{i + 1}</span>
                  <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#1a2b49', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {c.cardLabel || c.cardId}
                  </div>
                  <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                    <span style={{ fontSize: 9, fontWeight: 700, background: '#EDF7F2', color: '#1B6B45', padding: '1px 6px', borderRadius: 8 }}>EF {c.ef}</span>
                    <span style={{ fontSize: 9, color: '#8a9ab5', fontWeight: 600 }}>{c.interval}d · {c.repetitions} reps</span>
                  </div>
                </div>
              ))
            }
          </div>
        </Panel>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  BKT  Panel
//  Data shape (from bktAnalytics):
//    { mastered, nearMastery, developing, emerging, avgMastery, avgPSlip, avgPGuess,
//      highSlipCards[], cardMasteryList[], setMastery[], totalCards }
// ─────────────────────────────────────────────────────────────────────────────

function BKTPanel({ data }) {
  if (!data) return (
    <div style={{ padding: '28px 0', textAlign: 'center', color: '#8a9ab5', fontSize: 12 }}>
      No BKT data yet — complete at least one session to see knowledge tracing analysis.
    </div>
  );

  const {
    mastered, nearMastery, developing, emerging,
    avgMastery, avgPSlip, highSlipCards, cardMasteryList, setMastery, totalCards
  } = data;

  const avgMasteryPct = Math.round(avgMastery * 100);
  const masteryColor  = avgMasteryPct >= 70 ? '#1B6B45' : avgMasteryPct >= 45 ? '#D4AC0D' : '#C0392B';

  const STATES = [
    { label: '✅ Mastered',     count: mastered,    color: '#1B6B45', bg: '#EDF7F2', note: 'P ≥ 80%' },
    { label: '🔵 Near Mastery', count: nearMastery, color: '#1A4A6E', bg: '#EBF4FB', note: '60–79%' },
    { label: '🟡 Developing',   count: developing,  color: '#D4AC0D', bg: '#FEF9E7', note: '40–59%' },
    { label: '🔴 Emerging',     count: emerging,    color: '#C0392B', bg: '#FDEDEC', note: '< 40%' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <InfoBox bg="#EDF7F2" border="#A9DFBF" color="#1B6B45">
        <strong>BKT (Bayesian Knowledge Tracing)</strong> estimates the probability the child
        has truly mastered each concept. It models <em>slipping</em> (knows but makes a mistake)
        and <em>guessing</em> (doesn't know but answers correctly).
      </InfoBox>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        <AlgStat icon="🎓" val={`${avgMasteryPct}%`} label="Avg Mastery" color={masteryColor} bg={`${masteryColor}12`} />
        <AlgStat icon="✅" val={mastered}    label="Mastered" color="#1B6B45" bg="#EDF7F2" sub="P ≥ 80%" />
        <AlgStat icon="⚠️" val={`${Math.round(avgPSlip * 100)}%`} label="Avg Slip Rate"
          color={avgPSlip > 0.15 ? '#C0392B' : '#D4AC0D'}
          bg={avgPSlip > 0.15 ? '#FDEDEC' : '#FEF9E7'}
          sub="forgetting risk" />
        <AlgStat icon="🃏" val={totalCards} label="Total Cards" color="#7B2D8B" bg="#F5EEF8" />
      </div>

      {/* Knowledge state distribution */}
      <Panel>
        <SectionHead label="Knowledge State Distribution" />
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 14 }}>
          {STATES.map(s => (
            <div key={s.label} style={{ background: s.bg, borderRadius: 10, padding: '10px 12px', border: `1.5px solid ${s.color}30`, textAlign: 'center' }}>
              <div style={{ fontSize: 20, fontWeight: 900, color: s.color, lineHeight: 1 }}>{s.count}</div>
              <div style={{ fontSize: 9, fontWeight: 700, color: s.color, textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 3 }}>{s.label}</div>
              <div style={{ fontSize: 9, color: '#8a9ab5', marginTop: 2 }}>{s.note}</div>
            </div>
          ))}
        </div>
        {/* Stacked bar */}
        <div style={{ display: 'flex', height: 12, borderRadius: 6, overflow: 'hidden', gap: 1 }}>
          {STATES.map(s => {
            const pct = totalCards > 0 ? (s.count / totalCards) * 100 : 0;
            return pct > 0
              ? <div key={s.label} title={`${s.label}: ${s.count} (${Math.round(pct)}%)`}
                  style={{ flex: pct, background: s.color, minWidth: 4, transition: 'flex .4s' }} />
              : null;
          })}
        </div>
        <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 9, color: '#8a9ab5', flexWrap: 'wrap' }}>
          {STATES.map(s => (
            <span key={s.label} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <span style={{ width: 7, height: 7, borderRadius: 2, background: s.color, display: 'inline-block' }} />
              {s.label}
            </span>
          ))}
        </div>
      </Panel>

      {/* Per-set BKT mastery */}
      {setMastery?.length > 0 && (
        <Panel>
          <SectionHead label="BKT Mastery by Flashcard Set" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {setMastery.map((s, i) => {
              const pct = Math.round(s.avgMastery * 100);
              const col = pct >= 70 ? '#1B6B45' : pct >= 45 ? '#D4AC0D' : '#C0392B';
              return (
                <div key={i}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#1a2b49', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {s.setTitle || 'Untitled'}
                    </span>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0, marginLeft: 10 }}>
                      <span style={{ fontSize: 9, color: '#8a9ab5' }}>{s.cardCount} cards</span>
                      <span style={{ fontSize: 12, fontWeight: 900, color: col }}>{pct}%</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', height: 7, borderRadius: 4, overflow: 'hidden', gap: 1 }}>
                    {[
                      { v: s.dist.mastered,    c: '#1B6B45' },
                      { v: s.dist.nearMastery, c: '#1A4A6E' },
                      { v: s.dist.developing,  c: '#D4AC0D' },
                      { v: s.dist.emerging,    c: '#C0392B' },
                    ].map((b, j) => b.v > 0
                      ? <div key={j} style={{ flex: b.v, background: b.c, minWidth: 3 }} />
                      : null
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* High slip risk */}
      {highSlipCards?.length > 0 && (
        <div style={{ background: 'white', borderRadius: 12, padding: '14px 18px', border: '1.5px solid #F1948A' }}>
          <SectionHead label="⚠️ High Slip Risk Cards (pSlip > 15%)" />
          <div style={{ fontSize: 10, color: '#8a9ab5', marginBottom: 10, lineHeight: 1.6 }}>
            Child knows these cards but is at risk of forgetting under pressure. Revisit soon.
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {highSlipCards.map((c, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px', borderRadius: 8, background: '#fef8f8', border: '1px solid #f9e0e0' }}>
                <div style={{ flex: 1, fontSize: 11, fontWeight: 700, color: '#1a2b49', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.cardLabel || c.cardId}
                </div>
                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                  <span style={{ fontSize: 9, fontWeight: 800, background: '#FDEDEC', color: '#C0392B', padding: '1px 6px', borderRadius: 8 }}>
                    slip {Math.round(c.pSlip * 100)}%
                  </span>
                  <span style={{ fontSize: 9, fontWeight: 700, background: '#EDF7F2', color: '#1B6B45', padding: '1px 6px', borderRadius: 8 }}>
                    mastery {Math.round(c.masteryProb * 100)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Full card mastery list */}
      <Panel>
        <SectionHead label="Per-Card Mastery List" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 320, overflowY: 'auto' }}>
          {cardMasteryList.map((c, i) => {
            const pct = Math.round(c.masteryProb * 100);
            const col = pct >= 80 ? '#1B6B45' : pct >= 60 ? '#1A4A6E' : pct >= 40 ? '#D4AC0D' : '#C0392B';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 600, color: '#3d4d63', width: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {c.cardLabel || c.cardId}
                </div>
                <div style={{ flex: 1 }}><MiniBar pct={pct} color={col} height={6} /></div>
                <span style={{ fontSize: 10, fontWeight: 900, color: col, minWidth: 32, textAlign: 'right', flexShrink: 0 }}>{pct}%</span>
                {c.pSlip > 0.15 && <span title="High slip risk" style={{ fontSize: 10, flexShrink: 0 }}>⚠️</span>}
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  ELO  Panel
//  Data shape (from eloAnalytics):
//    { avgChildRating, avgCardDifficulty, comfortZone, challenge,
//      diffTiers{easy,medium,hard}, cardRatings[], setElo[], totalCards }
// ─────────────────────────────────────────────────────────────────────────────

function ELOPanel({ data }) {
  if (!data) return (
    <div style={{ padding: '28px 0', textAlign: 'center', color: '#8a9ab5', fontSize: 12 }}>
      No ELO data yet — complete at least one session to see difficulty rating analysis.
    </div>
  );

  const { avgChildRating, avgCardDifficulty, comfortZone, challenge, diffTiers, cardRatings, setElo, totalCards } = data;
  const overallGap  = avgChildRating - avgCardDifficulty;
  const skillColor  = overallGap > 50 ? '#1B6B45' : overallGap > -50 ? '#D4AC0D' : '#C0392B';
  const comfortPct  = totalCards > 0 ? Math.round((comfortZone / totalCards) * 100) : 0;

  const statusMessage =
    overallGap > 100  ? '🏆 Child rating significantly exceeds card difficulty — consider harder sets'
    : overallGap > 30 ? '✅ Child is well-matched to current card difficulty'
    : overallGap > -30 ? '⚖️ Child and card ratings are closely matched — ideal challenge zone'
    : overallGap > -100 ? '📚 Cards are slightly harder than child level — needs more practice'
    : '⚠️ Cards are significantly harder — consider easier sets first';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <InfoBox bg="#F5EEF8" border="#D7BDE2" color="#6B2D8B">
        <strong>ELO Rating</strong> treats learning like a chess match — each card has a
        difficulty rating and the child has a skill rating. Correct answer → child rating up;
        wrong answer → card difficulty up. Reveals which cards are well-calibrated vs too hard/easy.
      </InfoBox>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        <AlgStat icon="🏅" val={avgChildRating}    label="Child Rating"      color="#1A4A6E" bg="#EBF4FB" sub="skill level" />
        <AlgStat icon="🎯" val={avgCardDifficulty} label="Card Difficulty"   color="#7B2D8B" bg="#F5EEF8" sub="card hardness" />
        <AlgStat icon="✅" val={`${comfortPct}%`}   label="Comfort Zone"      color="#1B6B45" bg="#EDF7F2" sub={`${comfortZone} cards`} />
        <AlgStat icon="💪" val={`${100 - comfortPct}%`} label="Challenge Zone"
          color={100 - comfortPct > 60 ? '#C0392B' : '#D4AC0D'}
          bg="#FFF3E0"
          sub={`${challenge} cards`} />
      </div>

      {/* Skill vs difficulty gauge */}
      <Panel>
        <SectionHead label="Skill vs Difficulty Balance" />
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#1A4A6E', lineHeight: 1 }}>{avgChildRating}</div>
            <div style={{ fontSize: 10, color: '#8a9ab5', fontWeight: 700, marginTop: 2 }}>Child Rating</div>
          </div>
          <div style={{ fontSize: 22, color: skillColor, fontWeight: 900 }}>
            {overallGap > 0 ? '>' : overallGap < 0 ? '<' : '='}
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: '#7B2D8B', lineHeight: 1 }}>{avgCardDifficulty}</div>
            <div style={{ fontSize: 10, color: '#8a9ab5', fontWeight: 700, marginTop: 2 }}>Card Difficulty</div>
          </div>
        </div>
        <div style={{ padding: '8px 12px', background: `${skillColor}12`, borderRadius: 8, border: `1.5px solid ${skillColor}30`, fontSize: 11, color: skillColor, fontWeight: 700, textAlign: 'center' }}>
          {statusMessage}
        </div>
      </Panel>

      {/* Difficulty tiers */}
      <Panel>
        <SectionHead label="Card Difficulty Tiers (ELO Calibrated)" />
        {[
          { label: '🟢 Easy    (ELO < 1400)',  count: diffTiers.easy,   color: '#1B6B45' },
          { label: '🟡 Medium  (1400 – 1550)', count: diffTiers.medium, color: '#D4AC0D' },
          { label: '🔴 Hard    (ELO > 1550)',  count: diffTiers.hard,   color: '#C0392B' },
        ].map((t, i) => {
          const pct = totalCards > 0 ? (t.count / totalCards) * 100 : 0;
          return (
            <div key={i} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: '#1a2b49' }}>{t.label}</span>
                <span style={{ fontSize: 11, fontWeight: 900, color: t.color }}>{t.count} ({Math.round(pct)}%)</span>
              </div>
              <MiniBar pct={pct} color={t.color} />
            </div>
          );
        })}
      </Panel>

      {/* Per-set ELO */}
      {setElo?.length > 0 && (
        <Panel>
          <SectionHead label="ELO Summary by Flashcard Set" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {setElo.map((s, i) => {
              const gapColor = s.gap > 50 ? '#1B6B45' : s.gap > -50 ? '#D4AC0D' : '#C0392B';
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 9, background: '#fafbff', border: '1px solid #e8edf8' }}>
                  <div style={{ flex: 1, fontSize: 12, fontWeight: 700, color: '#1a2b49', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {s.setTitle || 'Untitled'}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, color: '#8a9ab5', fontWeight: 600 }}>👤{s.avgChildRating}</span>
                    <span style={{ fontSize: 10, color: '#8a9ab5' }}>vs</span>
                    <span style={{ fontSize: 10, color: '#8a9ab5', fontWeight: 600 }}>🃏{s.avgCardDifficulty}</span>
                    <span style={{ fontSize: 10, fontWeight: 900, color: gapColor, background: `${gapColor}15`, padding: '1px 7px', borderRadius: 8 }}>
                      {s.gap > 0 ? `+${s.gap}` : s.gap}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* Per-card table */}
      <Panel>
        <SectionHead label="Per-Card Rating Details" />
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 300, overflowY: 'auto' }}>
          {cardRatings.map((c, i) => {
            const gapColor = c.gap > 50 ? '#1B6B45' : c.gap > -50 ? '#D4AC0D' : '#C0392B';
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', borderRadius: 7, background: i % 2 === 0 ? '#fafbff' : 'white', border: '1px solid #f0f4f8' }}>
                <div style={{ flex: 1, fontSize: 11, fontWeight: 600, color: '#1a2b49', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.cardLabel || c.cardId}
                </div>
                <div style={{ display: 'flex', gap: 5, flexShrink: 0, fontSize: 9, fontWeight: 700 }}>
                  <span style={{ color: '#1A4A6E' }}>👤{c.eloChild}</span>
                  <span style={{ color: '#7B2D8B' }}>🃏{c.eloDifficulty}</span>
                  <span style={{ color: gapColor, background: `${gapColor}15`, padding: '1px 6px', borderRadius: 6 }}>
                    {c.gap > 0 ? `+${c.gap}` : c.gap}
                  </span>
                  <span style={{ color: '#8a9ab5' }}>{c.gap >= 0 ? 'Comfort' : 'Challenge'}</span>
                </div>
              </div>
            );
          })}
        </div>
      </Panel>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  LearningModulesSection
//  Shows worksheet / activitycard / storybook analytics from moduleAnalytics.
//
//  Props:
//    moduleAnalytics  — analytics.moduleAnalytics from the API response
//                       shape: { worksheet?: {...}, activitycard?: {...}, storybook?: {...} }
//    onNav            — same onNav as ChildrenView (to jump to a module)
// ─────────────────────────────────────────────────────────────────────────────

const MODULE_META = {
  worksheet:    { icon: '📝', label: 'Worksheets',      color: '#6B2D8B', bg: '#F5EEF8', navId: 'worksheet-learn'  },
  activitycard: { icon: '🖼️', label: 'Activity Cards',  color: '#0D5C63', bg: '#E0F2F1', navId: 'activity-learn'   },
  storybook:    { icon: '📖', label: 'Story Books',      color: '#8B4513', bg: '#FFF8F0', navId: 'storybook-learn'  },
};

function barColor(pct) {
  if (pct == null) return '#ccc';
  return pct >= 70 ? '#1B6B45' : pct >= 40 ? '#D4AC0D' : '#C0392B';
}

function ModulePanel({ mt, data, onNav }) {
  const meta = MODULE_META[mt];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      <InfoBox bg={meta.bg} border={`${meta.color}40`} color={meta.color}>
        <strong>{meta.icon} {meta.label}</strong> — interactive sessions tracked across all content.
        Accuracy is teacher-rated per item (🔴 needs help · 🟡 with prompt · 🟢 got it).
      </InfoBox>

      {/* ── 4 stat pills ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10 }}>
        <AlgStat icon="🗓️"
          val={data.sessionCount}
          label="Sessions"
          color={meta.color} bg={meta.bg} />
        <AlgStat icon="📋"
          val={data.totalItems}
          label="Items Done"
          color="#1A4A6E" bg="#EBF4FB"
          sub="total rated" />
        <AlgStat
          icon="🎯"
          val={data.avgAccuracy != null ? `${data.avgAccuracy}%` : '—'}
          label="Avg Accuracy"
          color={barColor(data.avgAccuracy)}
          bg={data.avgAccuracy != null ? `${barColor(data.avgAccuracy)}12` : '#f5f5f5'}
          sub={data.level?.label || ''} />
        <AlgStat
          icon={data.avgResponseMs ? '⚡' : '⏱️'}
          val={data.avgDuration != null ? `${data.avgDuration}m` : '—'}
          label="Avg Duration"
          color="#7B2D8B" bg="#F5EEF8"
          sub={data.avgResponseMs ? `⚡${Math.round(data.avgResponseMs / 100) / 10}s/item` : 'per session'} />
      </div>

      {/* ── Accuracy trend (last 10 sessions) ── */}
      {data.trend?.length > 0 && (
        <Panel>
          <SectionHead label={`Accuracy Trend — Last ${data.trend.length} Sessions`} />
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 72 }}>
            {data.trend.map((d, i) => (
              <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                <div style={{ fontSize: 9, fontWeight: 700, color: barColor(d.accuracy) }}>
                  {d.accuracy != null ? `${d.accuracy}%` : '—'}
                </div>
                <div
                  style={{
                    width: '100%', borderRadius: '3px 3px 0 0',
                    height: d.accuracy != null ? `${Math.max(5, d.accuracy * 0.55)}px` : '3px',
                    background: d.accuracy != null ? barColor(d.accuracy) : '#eee',
                    transition: 'height .3s',
                  }}
                  title={`${d.setTitle} · ${d.items} items${d.durationMin ? ` · ${d.durationMin}m` : ''}`}
                />
                <div style={{ fontSize: 7, color: '#8a9ab5', textAlign: 'center', whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {d.date ? new Date(d.date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }) : '—'}
                </div>
                {d.avgResponseMs > 0 && (
                  <div style={{ fontSize: 7, color: '#0D5C63', fontWeight: 700 }}>
                    ⚡{Math.round(d.avgResponseMs / 100) / 10}s
                  </div>
                )}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 12, marginTop: 8, fontSize: 9, color: '#8a9ab5', flexWrap: 'wrap' }}>
            {[['#1B6B45', '≥70% Good'], ['#D4AC0D', '40–69% Fair'], ['#C0392B', '<40% Needs support']].map(([c, l]) => (
              <span key={l} style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                <span style={{ width: 7, height: 7, borderRadius: 2, background: c, display: 'inline-block' }} />{l}
              </span>
            ))}
          </div>
        </Panel>
      )}

      {/* ── Top content items ── */}
      {data.topContent?.length > 0 && (
        <Panel>
          <SectionHead label="Most Used Content" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
            {data.topContent.map((c, i) => {
              const accCol = barColor(c.avgAccuracy);
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', borderRadius: 9, background: '#fafbff', border: '1px solid #e8edf8' }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{meta.icon}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#1a2b49', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.title || 'Untitled'}
                    </div>
                    {c.lastUsed && (
                      <div style={{ fontSize: 9, color: '#8a9ab5', marginTop: 1 }}>
                        Last used {new Date(c.lastUsed).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                    <span style={{ fontSize: 10, fontWeight: 700, color: meta.color, background: meta.bg, padding: '1px 7px', borderRadius: 8 }}>
                      {c.sessions}× used
                    </span>
                    {c.avgAccuracy != null && (
                      <span style={{ fontSize: 10, fontWeight: 700, color: accCol, background: `${accCol}15`, padding: '1px 7px', borderRadius: 8 }}>
                        {c.avgAccuracy}%
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </Panel>
      )}

      {/* ── Accuracy breakdown label ── */}
      {data.avgAccuracy != null && (
        <div style={{ borderRadius: 10, padding: '10px 14px', background: `${barColor(data.avgAccuracy)}10`, border: `1.5px solid ${barColor(data.avgAccuracy)}30`, fontSize: 11, color: barColor(data.avgAccuracy), fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>
            {data.avgAccuracy >= 80 ? '🏆' : data.avgAccuracy >= 60 ? '📈' : data.avgAccuracy >= 40 ? '📊' : '🔧'}
          </span>
          <span>
            {data.level?.label} level · {data.avgAccuracy}% avg accuracy across {data.sessionCount} session{data.sessionCount !== 1 ? 's' : ''}.
            {data.avgAccuracy >= 80  ? ' Excellent performance — consider advancing to harder content.'
             : data.avgAccuracy >= 60 ? ' Good progress — regular practice will build fluency.'
             : data.avgAccuracy >= 40 ? ' Emerging skills — more repetition with prompting support needed.'
             : ' Foundation stage — consider simpler content or more scaffolding.'}
          </span>
        </div>
      )}

      {/* ── CTA: Jump to module ── */}
      {onNav && (
        <button
          onClick={() => onNav(meta.navId)}
          style={{ padding: '10px', background: meta.color, color: 'white', border: 'none', borderRadius: 9, fontWeight: 700, fontSize: 12, cursor: 'pointer', boxShadow: `0 4px 16px ${meta.color}33` }}>
          {meta.icon} Start {meta.label} Session
        </button>
      )}
    </div>
  );
}

export function LearningModulesSection({ moduleAnalytics, onNav }) {
  const [activeTab, setActiveTab] = useState('worksheet');

  // Only show tabs where data exists
  const availableTabs = Object.keys(MODULE_META).filter(mt => moduleAnalytics?.[mt]);

  if (!availableTabs.length) {
    return (
      <div style={{ background: 'white', borderRadius: 13, padding: '16px 20px', border: '1.5px solid #e2e8f0' }}>
        <div style={{ fontWeight: 800, fontSize: 11, color: '#8a9ab5', letterSpacing: 0.7, textTransform: 'uppercase', marginBottom: 10 }}>
          Learning Module Activity
        </div>
        <div style={{ textAlign: 'center', padding: '24px 0', color: '#8a9ab5', fontSize: 12 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>📋</div>
          No worksheet, activity card, or story book sessions yet.
          <br />Start a learning module session to see activity analysis here.
          <div style={{ display: 'flex', gap: 8, marginTop: 14, justifyContent: 'center', flexWrap: 'wrap' }}>
            {Object.entries(MODULE_META).map(([mt, m]) => (
              <button key={mt} onClick={() => onNav?.(m.navId)}
                style={{ padding: '7px 14px', background: m.bg, color: m.color, border: `1.5px solid ${m.color}40`, borderRadius: 8, fontWeight: 700, fontSize: 11, cursor: 'pointer' }}>
                {m.icon} {m.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Default to first tab that has data
  const currentTab = availableTabs.includes(activeTab) ? activeTab : availableTabs[0];
  const meta        = MODULE_META[currentTab];

  return (
    <div style={{ background: 'white', borderRadius: 13, padding: '16px 20px', border: '1.5px solid #e2e8f0' }}>

      {/* Section header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 11, color: '#8a9ab5', letterSpacing: 0.7, textTransform: 'uppercase', marginBottom: 4 }}>
          Learning Module Activity · Worksheets · Activity Cards · Story Books
        </div>
        <div style={{ fontSize: 11, color: '#8a9ab5', lineHeight: 1.5 }}>
          Accuracy and engagement data from interactive learning activities — tracked separately from flashcard sessions.
        </div>
      </div>

      {/* Module tab bar — only shows tabs with data */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
        {availableTabs.map(mt => {
          const m    = MODULE_META[mt];
          const data = moduleAnalytics[mt];
          const isActive = currentTab === mt;
          return (
            <button key={mt} onClick={() => setActiveTab(mt)}
              style={{
                flex: 1, minWidth: 100, padding: '10px 12px', borderRadius: 10,
                border: `2px solid ${isActive ? m.color : '#e2e8f0'}`,
                background: isActive ? m.color : 'white',
                color: isActive ? 'white' : '#8a9ab5',
                cursor: 'pointer', textAlign: 'center', transition: 'all .15s',
              }}>
              <div style={{ fontSize: 18, marginBottom: 2 }}>{m.icon}</div>
              <div style={{ fontWeight: 800, fontSize: 11 }}>{m.label}</div>
              <div style={{ fontSize: 9, opacity: 0.8, marginTop: 1 }}>{data.sessionCount} sessions</div>
              {data.avgAccuracy != null && (
                <div style={{ fontSize: 10, fontWeight: 900, opacity: isActive ? 1 : 0.7, marginTop: 2 }}>
                  {data.avgAccuracy}% acc
                </div>
              )}
            </button>
          );
        })}
      </div>

      <ModulePanel mt={currentTab} data={moduleAnalytics[currentTab]} onNav={onNav} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
//  AlgorithmAnalysisSection  — the tab switcher  (the only exported component)
//
//  Props:
//    summary  — the full analytics state object from ChildrenView
//              e.g. the state variable set by:
//              const [summary, setSummary] = useState(null);
//              ...
//              const data = await res.json();
//              setSummary(data);
//
//  Place this at the bottom of AnalyticsTab in ChildrenView.jsx.
// ─────────────────────────────────────────────────────────────────────────────

export function AlgorithmAnalysisSection({ summary }) {
  const [algTab, setAlgTab] = useState('sm2');

  const sm2 = summary?.analytics?.sm2Analytics;
  const bkt = summary?.analytics?.bktAnalytics;
  const elo = summary?.analytics?.eloAnalytics;

  const ALG_TABS = [
    { id: 'sm2', icon: '📅', label: 'SM-2', sub: 'Spaced Repetition' },
    { id: 'bkt', icon: '🧠', label: 'BKT',  sub: 'Knowledge Tracing' },
    { id: 'elo', icon: '🏅', label: 'ELO',  sub: 'Difficulty Rating' },
  ];

  return (
    <div style={{ background: 'white', borderRadius: 13, padding: '16px 20px', border: '1.5px solid #e2e8f0' }}>

      {/* Section header */}
      <div style={{ marginBottom: 14 }}>
        <div style={{ fontWeight: 800, fontSize: 11, color: '#8a9ab5', letterSpacing: 0.7, textTransform: 'uppercase', marginBottom: 4 }}>
          Per-Algorithm Deep Analysis · Flashcards Only
        </div>
        <div style={{ fontSize: 11, color: '#8a9ab5', lineHeight: 1.5 }}>
          How each learning algorithm evaluates this child's flashcard knowledge —
          SM-2 for retention scheduling, BKT for knowledge probability, ELO for skill-vs-difficulty balance.
          Worksheet, Activity Card, and Story Book analytics are in the section above.
        </div>
      </div>

      {/* Algorithm tab bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {ALG_TABS.map(t => (
          <button key={t.id} onClick={() => setAlgTab(t.id)}
            style={{
              flex: 1, padding: '9px 10px', borderRadius: 10,
              border: `2px solid ${algTab === t.id ? '#1a2b49' : '#e2e8f0'}`,
              background: algTab === t.id ? '#1a2b49' : 'white',
              color: algTab === t.id ? 'white' : '#8a9ab5',
              cursor: 'pointer', textAlign: 'center', transition: 'all .15s'
            }}>
            <div style={{ fontSize: 16, marginBottom: 2 }}>{t.icon}</div>
            <div style={{ fontWeight: 800, fontSize: 12 }}>{t.label}</div>
            <div style={{ fontSize: 9, opacity: 0.75, marginTop: 1 }}>{t.sub}</div>
          </button>
        ))}
      </div>

      {algTab === 'sm2' && <SM2Panel data={sm2} />}
      {algTab === 'bkt' && <BKTPanel data={bkt} />}
      {algTab === 'elo' && <ELOPanel data={elo} />}
    </div>
  );
}

export default AlgorithmAnalysisSection;